import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, HelpCircle, PlayCircle } from 'lucide-react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

/**
 * Shared layout for all tool pages. Bold dark + green theme.
 * Props: title, description, icon (React node or Lucide icon), guide, children
 *
 * guide: optional object { intro: string, steps: string[], videoId?: string }
 *   When provided, renders a "How to use this tool" button that opens a tutorial dialog.
 */
function ToolLayout({ title, description, icon: Icon, guide, children }) {
  const [pulsing, setPulsing] = useState(true);

  useEffect(() => {
    if (!guide) return;
    const timer = setTimeout(() => setPulsing(false), 5000);
    return () => clearTimeout(timer);
  }, [guide]);

  const handleGuideOpen = () => {
    setPulsing(false);
  };

  return (
    <div
      className="min-h-screen bg-page py-6 px-4"
      style={{ paddingTop: 'var(--page-padding)', paddingBottom: 'var(--page-padding)' }}
    >
      <div
        className="mx-auto max-w-content"
        style={{
          maxWidth: 'var(--content-max-width)',
          paddingLeft: 'var(--page-padding)',
          paddingRight: 'var(--page-padding)',
        }}
      >
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-theme-accent hover:text-theme-accent-hover font-medium mb-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Tool Hub
          </Link>
          <div className="flex items-start gap-3">
            {Icon && (
              <div className="flex-shrink-0 text-theme-accent">
                {typeof Icon === 'function' ||
                (typeof Icon === 'object' &&
                  Icon !== null &&
                  typeof Icon.$$typeof !== 'undefined') ? (
                  <Icon className="w-10 h-10" />
                ) : (
                  Icon
                )}
              </div>
            )}
            <div>
              <h1 className="text-4xl font-bold text-text-primary mb-2">{title}</h1>
              {description && <p className="text-lg text-text-muted">{description}</p>}
              {guide && (
                <Dialog onOpenChange={(open) => { if (open) handleGuideOpen(); }}>
                  <DialogTrigger asChild>
                    <button
                      className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-theme-accent/20 text-theme-accent hover:bg-theme-accent/30 font-medium transition-colors ${pulsing ? 'animate-pulse' : ''}`}
                    >
                      <HelpCircle className="w-5 h-5" />
                      How to use this tool
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-xl">How to use this tool</DialogTitle>
                      <DialogDescription>{guide.intro}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                      <ol className="list-decimal list-inside space-y-2 text-sm text-text-primary">
                        {guide.steps.map((step, i) => (
                          <li key={i} className="leading-relaxed">{step}</li>
                        ))}
                      </ol>
                      {guide.videoId ? (
                        <div className="aspect-video rounded-lg overflow-hidden">
                          <iframe
                            width="100%"
                            height="100%"
                            src={`https://www.youtube.com/embed/${guide.videoId}`}
                            title="Video tutorial"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      ) : (
                        <div className="aspect-video rounded-lg border-2 border-dashed border-gray-600 flex flex-col items-center justify-center text-text-muted">
                          <PlayCircle className="w-12 h-12 mb-2 opacity-40" />
                          <p className="text-sm font-medium">Video tutorial coming soon</p>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export default ToolLayout;
